import React from "react";
import { Theme, alpha } from "@mui/material/styles";
import { Box, InputAdornment, Paper, TextField, ToggleButton, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { makeStyles } from "tss-react/mui";
import { keyframes } from "tss-react";
import { LinkKind, MapCard, MapIssue, MapLink, MapNode, NodeKind, NodeState } from "./types";

export const LINK_COLORS: Record<LinkKind, string> = {
  proxy: "#005f98",
  api: "#00897b",
  ai: "#e07b00",
  data: "#7b4fc9",
  event: "#c2185b",
  monitor: "#43a047",
  dns: "#1565c0",
  redirect: "#d84315",
  manage: "#6a1b9a",
};

const LINK_LABELS: Record<LinkKind, string> = {
  proxy: "Network / proxy",
  api: "API",
  ai: "AI",
  data: "Data",
  event: "Events",
  monitor: "Monitoring",
  dns: "DNS",
  redirect: "Redirect 301",
  manage: "Managed by",
};

// A kind filter also lights the nodes of the matching kinds, even without a link of that kind
const KIND_NODES: Record<LinkKind, NodeKind[]> = {
  proxy: ["proxy"],
  api: ["app", "client"],
  ai: ["ai"],
  data: ["db", "queue"],
  event: ["queue"],
  monitor: ["monitor"],
  dns: ["dns"],
  redirect: ["site"],
  manage: ["security"],
};

export const STATE_COLORS: Record<NodeState, string> = {
  up: "#43a047",
  warn: "#e5ac58",
  down: "#e53935",
  disabled: "#90a4ae",
  stopped: "#9e9e9e",
};

const KIND_COLORS: Record<NodeKind, string> = {
  app: "#00897b",
  ai: "#e07b00",
  db: "#7b4fc9",
  queue: "#c2185b",
  proxy: "#005f98",
  monitor: "#43a047",
  security: "#546e7a",
  client: "#0277bd",
  job: "#6d4c41",
  cloud: "#0078d4",
  dns: "#1565c0",
  site: "#2e7d32",
  external: "#757575",
};

const flow = keyframes`
  to { stroke-dashoffset: -24; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  stats: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  stat: {
    minWidth: 92,
    padding: theme.spacing(1, 1.5),
    borderRadius: 10,
    background: alpha(theme.palette.primary.main, 0.06),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    "& b": {
      display: "block",
      fontSize: 22,
      lineHeight: 1.1,
      color: theme.palette.primary.main,
    },
    "& span": {
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: theme.palette.text.secondary,
    },
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: 0,
    zIndex: 5,
  },
  kindButton: {
    textTransform: "none",
    padding: "3px 10px",
    borderRadius: "8px !important",
    border: `1px solid ${theme.palette.divider} !important`,
    "& i": {
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: 3,
      marginRight: 6,
    },
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginLeft: "auto",
    fontSize: 12,
    color: theme.palette.text.secondary,
    "& i": {
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: 3,
      marginRight: 5,
      verticalAlign: -1,
    },
  },
  canvas: {
    position: "relative",
    padding: theme.spacing(1, 0, 2),
  },
  grid: {
    display: "grid",
    columnGap: 48,
    alignItems: "start",
    [theme.breakpoints.down("lg")]: {
      gridTemplateColumns: "repeat(2, minmax(220px, 1fr)) !important",
      rowGap: 32,
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr !important",
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: 28,
  },
  bottom: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: 28,
    marginTop: 36,
    paddingTop: 24,
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  svg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    overflow: "visible",
    zIndex: 2,
  },
  path: {
    fill: "none",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeDasharray: "6 6",
    animation: `${flow} 0.9s linear infinite`,
  },
  faint: {
    strokeWidth: 1,
    strokeDasharray: "none",
    animation: "none",
    opacity: 0.18,
  },
  assumedPath: {
    strokeDasharray: "2 6",
  },
  card: {
    position: "relative",
    borderRadius: 14,
    overflow: "hidden",
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 2px rgba(16,24,40,.06), 0 4px 14px rgba(16,24,40,.05)",
    transition: "box-shadow 160ms, opacity 160ms, transform 160ms",
    animation: `${fadeIn} 360ms ease both`,
  },
  cardActive: {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}, 0 10px 28px rgba(16,24,40,.12)`,
    transform: "translateY(-1px)",
  },
  cardHead: {
    padding: theme.spacing(1.25, 1.5, 1),
    cursor: "pointer",
    borderTop: "4px solid",
  },
  title: {
    fontWeight: 700,
    fontSize: 15.5,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    lineHeight: 1.25,
  },
  badge: {
    height: 20,
    lineHeight: "20px",
    padding: "0 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
  },
  mono: {
    fontFamily: "'JetBrains Mono', 'Cascadia Code', Consolas, monospace",
  },
  subtitle: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  description: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
    lineHeight: 1.35,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 8,
    "& span": {
      fontSize: 11,
      padding: "1px 8px",
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
    },
  },
  nodes: {
    padding: theme.spacing(0.5, 1, 1.25),
  },
  group: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
    margin: theme.spacing(1, 0.5, 0.5),
  },
  node: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 8px",
    margin: "2px 0",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    transition: "background 120ms, opacity 160ms, border-color 120ms",
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.06),
    },
  },
  nodeName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tag: {
    fontSize: 11.5,
    padding: "1px 6px",
    borderRadius: 6,
    background: theme.palette.grey[100],
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
  },
  dot: {
    flex: "none",
    width: 8,
    height: 8,
    borderRadius: "50%",
  },
  active: {
    background: alpha(theme.palette.primary.main, 0.1),
    fontWeight: 600,
  },
  related: {
    background: alpha(theme.palette.primary.main, 0.05),
  },
  dim: {
    opacity: 0.28,
  },
  stopped: {
    color: theme.palette.text.disabled,
    textDecoration: "line-through",
  },
  disabled: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  footer: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    background: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  detail: {
    position: "fixed",
    right: 24,
    bottom: 24,
    width: 340,
    maxWidth: "calc(100vw - 48px)",
    maxHeight: "60vh",
    overflowY: "auto",
    zIndex: 20,
    borderRadius: 14,
    padding: theme.spacing(2),
    boxShadow: "0 18px 48px rgba(16,24,40,.22)",
    animation: `${fadeIn} 200ms ease both`,
  },
  relation: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
    fontSize: 12.5,
    padding: "3px 0",
    cursor: "pointer",
    "&:hover span": {
      textDecoration: "underline",
    },
  },
  issues: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: theme.spacing(1),
  },
  issue: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    padding: theme.spacing(1, 1.25),
    borderRadius: 10,
    fontSize: 12.5,
    cursor: "pointer",
    background: alpha("#e5ac58", 0.1),
    border: `1px solid ${alpha("#e5ac58", 0.35)}`,
    "&:hover": {
      background: alpha("#e5ac58", 0.18),
    },
  },
}));

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function curve(a: Rect, b: Rect): string {
  const ac = a.x + a.w / 2;
  const bc = b.x + b.w / 2;
  const ay = a.y + a.h / 2;
  const by = b.y + b.h / 2;
  // Same column: leave and enter on the right side with a loop
  if (Math.abs(ac - bc) < (a.w + b.w) / 4) {
    const x1 = a.x + a.w;
    const x2 = b.x + b.w;
    const dx = 28 + Math.min(80, Math.abs(by - ay) * 0.18);
    return `M${x1},${ay} C${x1 + dx},${ay} ${x2 + dx},${by} ${x2},${by}`;
  }
  const right = bc > ac;
  const x1 = right ? a.x + a.w : a.x;
  const x2 = right ? b.x : b.x + b.w;
  const dx = Math.max(36, Math.abs(x2 - x1) * 0.45) * (right ? 1 : -1);
  return `M${x1},${ay} C${x1 + dx},${ay} ${x2 - dx},${by} ${x2},${by}`;
}

export interface ArchMapProps {
  title: string;
  subtitle?: React.ReactNode;
  stats?: { label: string; value: number | string }[];
  cards: MapCard[];
  /** Card ids per column of the main grid */
  columns: string[][];
  /** CSS grid-template-columns of the main grid */
  gridTemplate?: string;
  /** Card ids of the row below the grid */
  bottom?: string[];
  bottomTitle?: string;
  links: MapLink[];
  legend?: { label: string; color: string }[];
  issues?: MapIssue[];
}

export const ArchMap: React.FunctionComponent<ArchMapProps> = ({
  title,
  subtitle,
  stats,
  cards,
  columns,
  gridTemplate,
  bottom,
  bottomTitle,
  links,
  legend,
  issues,
}) => {
  const { classes, cx } = useStyles();
  const canvasRef = React.useRef<HTMLDivElement>(null);
  const elements = React.useRef(new Map<string, HTMLElement>());
  const [hover, setHover] = React.useState<string | null>(null);
  const [pinned, setPinned] = React.useState<string | null>(null);
  const [kindFocus, setKindFocus] = React.useState<LinkKind | null>(null);
  const [query, setQuery] = React.useState("");
  const [showAll, setShowAll] = React.useState(false);
  const [paths, setPaths] = React.useState<{ key: string; d: string; link: MapLink; strong: boolean }[]>([]);
  const [size, setSize] = React.useState(0);

  // Lookups for the graph
  const index = React.useMemo(() => {
    const cardById = new Map(cards.map((c) => [c.id, c]));
    const nodeById = new Map<string, MapNode>();
    const cardOf = new Map<string, MapCard>();
    cards.forEach((c) =>
      c.nodes.forEach((n) => {
        nodeById.set(n.id, n);
        cardOf.set(n.id, c);
      }),
    );
    return { cardById, nodeById, cardOf };
  }, [cards]);

  const nameOf = (id: string) => index.nodeById.get(id)?.name ?? index.cardById.get(id)?.title ?? id;

  const active = hover ?? pinned;
  const usedKinds = React.useMemo(
    () => (Object.keys(LINK_LABELS) as LinkKind[]).filter((k) => links.some((l) => l.kind === k)),
    [links],
  );

  // Elements that belong to the active element: a card stands for all its nodes; a kind filter for its nodes
  const focus = React.useMemo(() => {
    if (active) {
      const card = index.cardById.get(active);
      const node = index.nodeById.get(active);
      return new Set([active, ...(card ? card.nodes.map((n) => n.id) : []), ...(node?.related ?? [])]);
    }
    if (kindFocus) {
      const set = new Set<string>();
      links.filter((l) => l.kind === kindFocus).forEach((l) => {
        set.add(l.from);
        set.add(l.to);
      });
      index.nodeById.forEach((n) => {
        if (KIND_NODES[kindFocus].includes(n.kind)) set.add(n.id);
      });
      return set;
    }
    return null;
  }, [active, kindFocus, index, links]);

  const activeLinks = React.useMemo(() => {
    if (active && focus) return links.filter((l) => focus.has(l.from) || focus.has(l.to));
    if (kindFocus) return links.filter((l) => l.kind === kindFocus);
    return [];
  }, [active, focus, kindFocus, links]);

  const related = React.useMemo(() => {
    if (!focus) return null;
    const set = new Set(focus);
    activeLinks.forEach((l) => {
      set.add(l.from);
      set.add(l.to);
    });
    // Keep the cards of related nodes lit
    Array.from(set).forEach((id) => {
      const card = index.cardOf.get(id);
      if (card) set.add(card.id);
    });
    return set;
  }, [focus, activeLinks, index]);

  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const set = new Set<string>();
    cards.forEach((c) => {
      const cardHit = [c.title, c.subtitle ?? "", c.description ?? ""].some((v) => v.toLowerCase().includes(q));
      c.nodes.forEach((n) => {
        const values = [n.name, n.tag ?? "", n.group ?? "", n.note ?? "", ...(n.details ?? []).map((d) => d[1])];
        if (cardHit || values.some((v) => v.toLowerCase().includes(q))) {
          set.add(n.id);
          set.add(c.id);
        }
      });
    });
    return set;
  }, [query, cards]);

  const dimmed = (id: string) => {
    if (related) return !related.has(id);
    if (matches) return !matches.has(id);
    return false;
  };

  // Recalculate the link curves whenever the selection, the filters or the layout change
  React.useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const base = canvas.getBoundingClientRect();
    const rectOf = (id: string): Rect | undefined => {
      const el = elements.current.get(id);
      if (!el) return undefined;
      const r = el.getBoundingClientRect();
      return { x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height };
    };
    const strong = new Set(activeLinks);
    const drawn = showAll ? links : activeLinks;
    const next: typeof paths = [];
    drawn.forEach((link, i) => {
      const a = rectOf(link.from);
      const b = rectOf(link.to);
      if (a && b) next.push({ key: `${link.from}-${link.to}-${i}`, d: curve(a, b), link, strong: strong.has(link) });
    });
    setPaths(next);
  }, [activeLinks, links, showAll, size]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setSize((n) => n + 1));
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPinned(null);
        setKindFocus(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const interact = (id: string) => ({
    ref: (el: HTMLElement | null) => {
      if (el) elements.current.set(id, el);
      else elements.current.delete(id);
    },
    onMouseEnter: () => setHover(id),
    onMouseLeave: () => setHover((h) => (h === id ? null : h)),
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      setPinned((p) => (p === id ? null : id));
    },
  });

  const renderNode = (node: MapNode, previous?: MapNode) => {
    const state = node.state ?? "up";
    const isFocus = focus?.has(node.id);
    return (
      <React.Fragment key={node.id}>
        {node.group && node.group !== previous?.group && <div className={classes.group}>{node.group}</div>}
        <div
          {...interact(node.id)}
          className={cx(classes.node, {
            [classes.active]: isFocus,
            [classes.related]: !isFocus && related?.has(node.id),
            [classes.dim]: dimmed(node.id),
          })}
          style={{ borderLeftColor: isFocus ? KIND_COLORS[node.kind] : undefined }}
        >
          <span className={classes.dot} style={{ background: state === "up" ? KIND_COLORS[node.kind] : STATE_COLORS[state] }} />
          <span
            className={cx(classes.nodeName, {
              [classes.mono]: node.mono,
              [classes.stopped]: state === "stopped" || state === "down",
              [classes.disabled]: state === "disabled",
            })}
            style={node.mono ? { fontSize: 12 } : undefined}
          >
            {node.name}
          </span>
          {node.tag && <span className={cx(classes.tag, classes.mono)}>{node.tag}</span>}
        </div>
      </React.Fragment>
    );
  };

  const renderCard = (id: string) => {
    const card = index.cardById.get(id);
    if (!card) return null;
    return (
      <div
        key={id}
        className={cx(classes.card, {
          [classes.cardActive]: focus?.has(id),
          [classes.dim]: dimmed(id),
        })}
      >
        <div {...interact(id)} className={classes.cardHead} style={{ borderTopColor: card.accent }}>
          <div className={classes.title}>
            {card.title}
            {card.badge && (
              <span className={classes.badge} style={{ background: card.badge.color }}>
                {card.badge.label}
              </span>
            )}
          </div>
          {card.subtitle && <div className={cx(classes.subtitle, classes.mono)}>{card.subtitle}</div>}
          {card.description && <div className={classes.description}>{card.description}</div>}
          {card.chips && card.chips.length > 0 && (
            <div className={classes.chips}>
              {card.chips.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
          )}
        </div>
        <div className={classes.nodes}>{card.nodes.map((n, i) => renderNode(n, card.nodes[i - 1]))}</div>
        {card.footer && <div className={classes.footer}>{card.footer}</div>}
      </div>
    );
  };

  // Detail panel of the hovered or pinned element
  const detail = (() => {
    if (!active) return undefined;
    const node = index.nodeById.get(active);
    if (node) {
      const card = index.cardOf.get(active)!;
      return {
        title: node.name,
        subtitle: [card.title, card.subtitle].filter(Boolean).join(" · "),
        details: [...(node.tag ? [["Tag", node.tag] as [string, string]] : []), ...(node.details ?? [])],
        note: node.note,
        url: node.url,
      };
    }
    const card = index.cardById.get(active);
    if (card) {
      return {
        title: card.title,
        subtitle: card.subtitle ?? "",
        details: card.details ?? [],
        note: card.note ?? card.description,
        url: undefined,
      };
    }
    return undefined;
  })();

  return (
    <Box
      className={classes.root}
      onClick={() => {
        setPinned(null);
        setKindFocus(null);
      }}
    >
      <div className={classes.header}>
        <div>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </div>
        {stats && (
          <div className={classes.stats}>
            {stats.map((s) => (
              <div key={s.label} className={classes.stat}>
                <b>{s.value}</b>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={classes.toolbar} onClick={(e) => e.stopPropagation()}>
        <TextField
          size="small"
          placeholder="Search name, port, host …"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: 240, "& .MuiOutlinedInput-root": { height: 36, borderRadius: 2, background: "#fff" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {usedKinds.map((kind) => (
          <ToggleButton
            key={kind}
            size="small"
            value={kind}
            selected={kindFocus === kind}
            onChange={() => {
              setPinned(null);
              setKindFocus((k) => (k === kind ? null : kind));
            }}
            className={classes.kindButton}
          >
            <i style={{ background: LINK_COLORS[kind] }} />
            {LINK_LABELS[kind]}
          </ToggleButton>
        ))}
        <ToggleButton
          size="small"
          value="all"
          selected={showAll}
          onChange={() => setShowAll((v) => !v)}
          className={classes.kindButton}
        >
          Show all links
        </ToggleButton>
        {legend && (
          <div className={classes.legend}>
            {legend.map((l) => (
              <span key={l.label}>
                <i style={{ background: l.color }} />
                {l.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={classes.canvas} ref={canvasRef}>
        <svg className={classes.svg}>
          {paths.map(({ key, d, link, strong }) => (
            <path
              key={key}
              d={d}
              stroke={LINK_COLORS[link.kind]}
              className={cx(classes.path, {
                [classes.faint]: !strong,
                [classes.assumedPath]: strong && !!link.assumed,
              })}
            />
          ))}
        </svg>

        <div
          className={classes.grid}
          style={{ gridTemplateColumns: gridTemplate ?? `repeat(${columns.length}, minmax(220px, 1fr))` }}
        >
          {columns.map((column, i) => (
            <div key={i} className={classes.column}>
              {column.map(renderCard)}
            </div>
          ))}
        </div>

        {bottom && bottom.length > 0 && (
          <div className={classes.bottom}>
            {bottomTitle && (
              <div className={classes.sectionTitle} style={{ gridColumn: "1 / -1", marginBottom: 0 }}>
                {bottomTitle}
              </div>
            )}
            {bottom.map(renderCard)}
          </div>
        )}
      </div>

      {issues && issues.length > 0 && (
        <>
          <div className={classes.sectionTitle} style={{ marginTop: 24 }}>
            Open points ({issues.length})
          </div>
          <div className={classes.issues}>
            {issues.map((issue) => (
              <div
                key={issue.text}
                className={classes.issue}
                onMouseEnter={() => setHover(issue.target)}
                onMouseLeave={() => setHover(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinned(issue.target);
                  elements.current.get(issue.target)?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              >
                <span className={classes.dot} style={{ background: STATE_COLORS.warn, marginTop: 5 }} />
                {issue.text}
              </div>
            ))}
          </div>
        </>
      )}

      {detail && (
        <Paper className={classes.detail} onClick={(e) => e.stopPropagation()}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {detail.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" className={classes.mono}>
            {detail.subtitle}
          </Typography>
          {detail.details.length > 0 && (
            <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.4, fontSize: 12.5 }}>
              {detail.details.map(([k, v]) => (
                <React.Fragment key={k}>
                  <Box sx={{ color: "text.secondary" }}>{k}</Box>
                  <Box className={classes.mono} sx={{ wordBreak: "break-word" }}>
                    {v}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
          {detail.note && (
            <Box sx={{ mt: 1.25, fontSize: 12.5, p: 1, borderRadius: 2, background: alpha("#e5ac58", 0.12) }}>{detail.note}</Box>
          )}
          {detail.url && (
            <Box sx={{ mt: 1, fontSize: 12.5 }}>
              <a href={detail.url} target="_blank" rel="noreferrer" className={classes.mono}>
                {detail.url}
              </a>
            </Box>
          )}
          {activeLinks.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <div className={classes.sectionTitle}>Relations ({activeLinks.length})</div>
              {activeLinks.map((l, i) => {
                const outgoing = focus?.has(l.from);
                const other = outgoing ? l.to : l.from;
                const otherCard = index.cardOf.get(other);
                return (
                  <div key={i} className={classes.relation} onClick={() => setPinned(other)}>
                    <i style={{ color: LINK_COLORS[l.kind], fontStyle: "normal" }}>{outgoing ? "→" : "←"}</i>
                    <span>{nameOf(other)}</span>
                    {otherCard && otherCard !== index.cardOf.get(active!) && (
                      <Box component="small" sx={{ color: "text.disabled" }}>
                        {otherCard.title}
                      </Box>
                    )}
                    <Box component="small" sx={{ color: "text.secondary", ml: "auto", textAlign: "right" }}>
                      {l.label ?? LINK_LABELS[l.kind]}
                      {l.assumed && " · assumed"}
                    </Box>
                  </div>
                );
              })}
            </Box>
          )}
          {!pinned && (
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
              Click to pin · Esc to release
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};
